import { LOCALES } from '@/i18n';
import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SupportedLocale } from '@/types/supported-locale.type';
import { COUNTRY_FLAGS } from '@/constants/countryFlags';
import clsx from 'clsx';

interface ResourceLanguageSwitchProps {
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
  onLanguageChange?: (newLanguage: SupportedLocale) => void;
  shouldHighlight?: boolean;
}

const ResourceLanguageSwitch = ({
  language,
  setLanguage,
  onLanguageChange,
  shouldHighlight = true,
}: ResourceLanguageSwitchProps) => {
  const languageOptions = useMemo(
    () =>
      LOCALES.map((locale) => ({ label: locale.toUpperCase(), value: locale })),
    [],
  );

  return (
    <div
      className={clsx(
        'flex items-center px-1 gap-1 border rounded-lg cursor-pointer',
        {
          'border-warning-300 bg-warning-100': shouldHighlight,
          'border-brand-300 bg-brand-100': !shouldHighlight,
        },
      )}
    >
      <Select
        onValueChange={(value) => {
          setLanguage(value as SupportedLocale);
          onLanguageChange?.(value as SupportedLocale);
        }}
        defaultValue={language.slice(0, 2)}
      >
        <SelectTrigger className="w-full border-0 cursor-pointer">
          <SelectValue placeholder={language.toUpperCase()} />
        </SelectTrigger>
        <SelectContent>
          {languageOptions.map((lang) => (
            <SelectItem
              key={lang.value}
              value={lang.value}
              className="w-full cursor-pointer"
            >
              <img
                src={COUNTRY_FLAGS[lang.value]}
                alt={lang.label}
                className="size-5 rounded-full"
              />
              <span
                className={clsx({
                  'text-warning-600': shouldHighlight,
                  'text-brand-700': !shouldHighlight,
                })}
              >
                {lang.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ResourceLanguageSwitch;
